<template>
    <div class="bg-white shadow mb-64 rounded p-32 max-w-960 mx-auto">
        <div class="flex flex-col">
            <div
                v-for="field in defaultFields"
                :key="'field-' + field"
                class="field-wrapper"
            >
                <h2 v-if="field == 'name'"
                    class="text-body font-semibold pl-4 first-letter:uppercase mb-24">
                    {{ $t('jobs.field_groups.personal_information') }}
                </h2>
                <h2 v-else-if="field == 'curriculum_vitae'"
                    class="text-body font-semibold pl-4 first-letter:uppercase mt-24 mb-24">
                    {{ $t('jobs.field_groups.candidate_profile') }}
                </h2>
                <div class="field-wrapper-child flex items-center mb-12 pl-16 pr-8">
                    <div class="font-semibold text-body-sm text-black first-letter:uppercase mr-auto">
                        {{ $t('jobs.field_names.' + field) }}
                    </div>
                    <segmented-button
                        :id="field"
                        v-model="form.dynamic_form.input_states[field]"
                        :options="getSegmentedButtonOptions(field)"
                        :class="isRequiredField(field) ? 'opacity-50' : ''"
                    />
                </div>
            </div>
        </div>

        <div>
            <h2 class="text-body font-semibold pl-4 first-letter:uppercase mt-24 mb-24">
                {{ $t('jobs.create.headers.extra_questions') }}
            </h2>
            <draggable
                v-model="form.dynamic_form.questions"
                handle=".draggable"
                :item-key="(item, index) => `field-${index}`"
            >
                <template #item="{ element, index }">
                    <div class="field-wrapper">
                        <div class="field-wrapper-child flex items-center mb-12 pl-16 pr-8">
                            <div class="font-semibold text-body first-letter:uppercase mr-auto truncate">
                                {{ element?.question?.[locale] }}
                            </div>
                            <div class="flex items-center mr-16 pl-24">
                                <button class="mr-12" @click="startEditingField(index)">
                                    <svg-icon class="max-w-16 max-h-16 text-primary-80 hover:text-primary trans-ease" type="edit"></svg-icon>
                                </button>
                                <button class="mr-12" @click="removeField(index)">
                                    <svg-icon class="max-w-16 max-h-16 text-primary-80 hover:text-primary trans-ease" type="delete"></svg-icon>
                                </button>
                                <div class="draggable cursor-move">
                                    <svg-icon class="text-primary-20 max-h-16 max-w-16" type="drag-handle"></svg-icon>
                                </div>
                            </div>
                            <div v-if="form?.dynamic_form.questions[index]?.required" class="flex rounded shadow">
                                <label class="flex-none">
                                    <input
                                        v-model="form.dynamic_form.questions[index].required"
                                        class="hidden"
                                        type="checkbox"
                                    >
                                    <div
                                        :class="[ form.dynamic_form.questions[index].required ? 'bg-primary text-white border-primary' : 'bg-white border-primary-40 text-primary-80 hover:text-primary' ]"
                                        class="trans-ease cursor-pointer h-40 flex items-center px-16 font-semibold rounded-l border-t border-b-2 border-l-2"
                                    >
                                        {{ $t('jobs.input_state.required') }}
                                    </div>
                                </label>
                                <label class="flex-none">
                                    <input
                                        v-model="form.dynamic_form.questions[index].required"
                                        class="hidden"
                                        type="checkbox"
                                    >
                                    <div
                                        :class="[ !form.dynamic_form.questions[index].required ? 'bg-semantic-warning text-white border-sementic-warning-dark' : 'bg-white border-primary-40 text-primary-80 hover:text-primary']"
                                        class="trans-ease cursor-pointer h-40 flex items-center px-16 font-semibold rounded-r border-t border-b-2 border-r"
                                    >
                                        {{ $t('jobs.input_state.optional') }}
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </template>
            </draggable>
        </div>

        <button
            :class="[ Object.keys(form.dynamic_form.questions).length ? 'mt-24' : 'mt-8' ]"
            class="py-16 rounded border border-primary-20 bg-primary-5 w-full flex items-center justify-center group text-primary-60 hover:text-primary-80"
            @click="startAddingField"
            id="add-new-question"
        >
            <span class="flex items-center gap-8">
                <span class="p-4 rounded-32 bg-primary-60 group-hover:bg-primary-80 text-primary-5">
                    <svg-icon class="w-18 h-18" type="add" />
                </span>
                <span class="text-body font-semibold first-letter:uppercase -mb-px">
                    {{ $t('jobs.add_extra_question') }}
                </span>
            </span>
        </button>

        <checkbox :checked="form.save_as_default"
                  class="mt-24 mb-8 mr-8"
                  @change="form.save_as_default = !form.save_as_default">
            <span class="font-normal">{{ $t('jobs.application_form_template') }}</span>
        </checkbox>

        <div class="pt-32">
            <div class="flex items-center justify-end">
                <simple-button
                    :loading="form.submitting"
                    class="mr-8"
                    icon="save"
                    id="save-and-close"
                    @click="$emit('save', {todo:'close',form})">
                    {{ $t('jobs.save_and_close') }}
                </simple-button>
                <simple-button
                    :loading="form.submitting"
                    display="secondary"
                    look="primary"
                    icon="save"
                    id="save-and-next"
                    @click="$emit('save', {todo:'next',form})">
                    {{ $t('jobs.save_and_next') }}
                </simple-button>
            </div>
        </div>

        <form-field-editor v-if="addingField" @close="stopAddingField" @input="addField"></form-field-editor>
        <form-field-editor v-if="editingField != null" :resource="editingFieldResource" @close="stopEditingField" @input="editField"></form-field-editor>

    </div>
</template>

<script>
import Form from '../../etc/forms/Form';
import Job from '../../models/Job';
import SvgIcon from './SvgIcon.vue';
import FormFieldEditor from './FormFieldEditor.vue';
import SimpleButton from './SimpleButton.vue';
import {mapState} from 'pinia';
import {useMainStore} from '../../state/store';
import draggable from 'vuedraggable';
import Checkbox from '../forms/Checkbox';
import SegmentedButton from "./SegmentedButton.vue";

// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }

export default {
    components: {
        SegmentedButton,
        'svg-icon': SvgIcon,
        'form-field-editor': FormFieldEditor,
        'simple-button': SimpleButton,
        'draggable': draggable,
        Checkbox,
    },
    props: {
        job: {
            required: true,
            type: Job,
        },
    },
    data: function () {
        return {
            defaultFields: Object.keys(this.getFields()[0].value.input_states),
            form: this.generateForm(),
            addingField: false,
            editingField: null,
            companyDynamicForm: null,
            segmentedButtonOptions: ['required', 'optional', 'hidden'],
            segmentedButtonSharedClasses: 'bg-white border-primary-20 text-primary-80',
            segmentedButtonHoverClasses: 'hover:text-black hover:bg-primary-20',
            segmentedButtonSelectedClasses: {
                required: 'bg-semantic-info text-white border-primary-20',
                optional: 'bg-semantic-warning text-white border-primary-20',
                hidden: 'bg-semantic-error text-white border-primary-20',
            }
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale']),
        editingFieldResource() {
            if (this.editingField == null) {
                return null;
            }

            return this.form.dynamic_form.questions[this.editingField];
        },
        hasDynamicQuestions() {
            return this.form.dynamic_form.questions.length > 0;
        },
    },
    methods: {
        getFields() {
            return [
                {
                    name: 'dynamic_form', value: {
                        'input_states': {
                            'name': 'required',
                            'email': 'required',
                            'telephone': 'required',
                            'address': 'hidden',
                            'photo': 'hidden',
                            'birthdate': 'hidden',
                            'birthplace': 'hidden',
                            'rijksregister_nr': 'hidden',
                            'iban_nr': 'hidden',
                            'linkedin': 'hidden',
                            'curriculum_vitae': 'hidden',
                            'motivation_letter': 'hidden',
                            'education': 'hidden',
                            'experience': 'hidden',
                            'motivation': 'hidden',
                        },
                        questions: [],
                    },
                },
            ];
        },
        generateForm() {
            let fields = this.getFields();
            let extraFields = {};

            // always add the required fields
            this.getRequiredFields().forEach(
                field => extraFields[field] = 'required',
            );

            // always add some hidden fields for backward compatibility
            this.getHiddenFields().forEach(
                field => extraFields[field] = 'hidden',
            );

            fields[0].value.input_states = {
                ...fields[0].value.input_states,
                ...this.job.dynamic_form.input_states,
                ...extraFields,
            };
            fields[0].value.questions = [...this.job.dynamic_form.questions];

            return new Form(fields, null, {
                save_as_default: false,
            });
        },
        getHiddenFields() {
            return ['facebook', 'twitter', 'instagram'];
        },
        getRequiredFields() {
            return ['name', 'email'];
        },
        isRequiredField(field) {
            return this.getRequiredFields().includes(field);
        },
        startAddingField() {
            this.addingField = true;
        },
        stopAddingField() {
            this.addingField = false;
        },
        addField({data, success, error}) {

            let {question_type, question, disqualify_answer_no, required_answer_yes, options, multiple, required, key} = data;

            let field = {question_type, question, required, key};

            if (['free_text', 'multiple_choice'].includes(question_type)) {
                field['multiple'] = multiple;
            }

            if (question_type === 'multiple_choice') {
                field['options'] = options;
            }

            if (question_type === 'yes_no') {
                field['disqualify_answer_no'] = disqualify_answer_no;

                field['required_answer_yes'] = required_answer_yes;
            }

            this.$set(this.form.dynamic_form, 'questions', [...this.form.dynamic_form.questions, field]);

            return success();
        },
        startEditingField(fieldIndex) {
            this.editingField = fieldIndex;
        },
        stopEditingField() {
            this.editingField = null;
        },
        editField({data, success, error}) {
            let editedQuestions = [...this.form.dynamic_form.questions];
            editedQuestions[this.editingField] = data;
            this.$set(this.form.dynamic_form, 'questions', editedQuestions);

            return success();
        },
        removeField(index) {
            this.form.dynamic_form.questions.splice(index, 1);
        },
        getSegmentedButtonOptions(field) {
            let data = [];
            this.segmentedButtonOptions.forEach(value => {
                data.push({
                    label: this.$t('jobs.input_state.' + value),
                    value: value,
                    disabled: this.isRequiredField(field),
                    classes: (this.form.dynamic_form.input_states[field] === value ?
                            this.segmentedButtonSelectedClasses[value] :
                            this.segmentedButtonSharedClasses + ' ' + (!this.isRequiredField(field) ? this.segmentedButtonHoverClasses : '')
                    )
                })
            })
            return data;
        }
    },
};
</script>

<style scoped>
.field-wrapper:nth-of-type(odd) .field-wrapper-child {
    @apply rounded py-8 bg-primary-5;
}
</style>
